/** A conservative serial rate gate; configurable to keep contract tests fast. */
export class RateGate {
  private tail: Promise<void> = Promise.resolve();
  private lastStartedAt = Number.NEGATIVE_INFINITY;

  constructor(
    private readonly minimumIntervalMs: number,
    private readonly now: () => number = () => Date.now(),
  ) {}

  async enter(signal?: AbortSignal): Promise<void> {
    const previous = this.tail;
    let release!: () => void;
    this.tail = new Promise<void>((resolve) => { release = resolve; });
    await previous;
    try {
      throwIfAborted(signal);
      const delay = Math.max(0, this.minimumIntervalMs - (this.now() - this.lastStartedAt));
      if (delay) await abortableDelay(delay, signal);
      this.lastStartedAt = this.now();
    } finally {
      release();
    }
  }
}

function abortableDelay(milliseconds: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(resolve, milliseconds);
    signal?.addEventListener("abort", () => {
      clearTimeout(timeout);
      reject(signal.reason ?? new DOMException("Aborted", "AbortError"));
    }, { once: true });
  });
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw signal.reason ?? new DOMException("Aborted", "AbortError");
}

